import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import SearchPlaceholder from "../../../components/chat/SearchPlaceholder";
import { getSearchList } from "../../../api/messages/messagesApi";

import styles from "../css/SearchView.module.css";
import { createChatObj, formatImageUrl } from "../../../utils";
import { createNewChannel } from "../../../api/messages/messagesApi";

const SearchView = forwardRef(
  (
    {
      searchTerm,
      chats,
      setChat,
      setChats,
      messageInputRef,
      resetSearch,
      setShowSendBtn,
    },
    ref
  ) => {
    const [searchList, setSearchList] = useState([]);
    const [selectedIdx, setSelectedIdx] = useState(0);

    useEffect(() => {
      if (searchTerm) fetchSearchList();
      else setSearchList([]);
    }, [searchTerm]);

    useEffect(() => {
      const handleKeyDown = (e) => {
        if (searchList.length === 0) return;
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedIdx((prev) =>
            prev + 1 < searchList.length ? prev + 1 : 0
          );
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedIdx((prev) =>
            prev - 1 >= 0 ? prev - 1 : searchList.length - 1
          );
        }
        if (e.key === "Enter") {
          e.preventDefault();
          handleUserClick(searchList[selectedIdx]);
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [selectedIdx, searchList]);

    const fetchSearchList = async () => {
      const searchList = await getSearchList(searchTerm);
      setSearchList(searchList);
    };

    const handleUserClick = async (selectedUser) => {
      try {
        // Check if a chat already exists with this user
        const selectedChat = chats.find((chat) => {
          return (
            chat?.dm_info?.members.length === 1 &&
            chat?.dm_info?.members[0]?.dm_id === selectedUser.user_id
          );
        });

        if (selectedChat) {
          // If chat exists, set it as the active chat
          setChat(selectedChat);
        } else {
          // If no chat exists, create a new channel
          const newChannelId = await createNewChannel([selectedUser.user_id]);
          
          // Create a properly structured chat object
          const newChat = {
            id: Date.now(), // Temporary ID until we get the proper one
            channel: newChannelId,
            dm_info: {
              members: [
                {
                  dm_id: selectedUser.user_id,
                  dm_name: selectedUser.name,
                  profile_pic: selectedUser.profile_pic,
                }
              ]
            },
            last_message: null,
            unread_count: 0,
            created_at: new Date().toISOString(),
          };

          // Update chats list and set as active chat
          setChats((prev) => {
            const updatedChats = [newChat, ...prev];
            return updatedChats;
          });
          
          // Set the chat immediately
          setChat(newChat);
        }

        // Reset search and focus on message input after a short delay
        resetSearch();
        
        // Use setTimeout to ensure the chat is set before focusing
        setTimeout(() => {
          if (messageInputRef.current) {
            messageInputRef.current.focus();
          }
        }, 100);
        
      } catch (error) {
        console.error("Error creating/selecting chat:", error);
        // Handle error appropriately - maybe show a toast notification
      }
    };

    useImperativeHandle(ref, () => ({
      async handleSendMessage() {
        // Keep for potential future multi-user chat creation
        resetSearch();
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      },
    }));

    return (
      <div>
        <div className={styles.userList}>
          {searchList.map((user, idx) => (
            <div
              key={user.id}
              className={`${styles.user} ${
                selectedIdx === idx ? styles.highlight : ""
              }`}
              onClick={() => handleUserClick(user)}
            >
              <img
                className={styles.profilePic}
                src={formatImageUrl(user?.profile_pic)}
                alt="profile-pic"
              />
              <p>{user.name}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

export default SearchView;